import { createTRPCProxyClient } from "@trpc/client";
import type { AppRouter } from "@/lib/desktop-trpc-types";
import { createTrpcDirectLink } from "@/lib/exchange/trpc-direct";
import { createClient } from "../exchange/ws-client";

const WS_URL = "wss://lensflow-exchange.hemaoptical.com";
const TOKEN_URL = "https://lensflow-exchange.hemaoptical.com/auth/token";

async function getToken(): Promise<string> {
  const res = await fetch(TOKEN_URL, { method: "POST" });
  if (!res.ok) {
    throw new Error(`token failed: ${res.status}`);
  }
  const data = await res.json();
  return String((data as any).token || "");
}

async function createWebSocketClient() {
  return createClient({ url: WS_URL, token: await getToken() });
}


let trpcClient: ReturnType<typeof createTRPCProxyClient<AppRouter>> | null = null;
let peerIdCache: string | null = null;
export async function getOrCreateDesktopTrpcClient(peerId: string){
  if(!peerId) {
    throw new Error("Peer ID is required");
  }
  if(trpcClient && peerIdCache === peerId) {
    return trpcClient;
  }
  peerIdCache = peerId;
  trpcClient = createTRPCProxyClient<AppRouter>({
    links: [
      createTrpcDirectLink<AppRouter>({
        ws: await createWebSocketClient(),
        peerId,
        timeoutMs: 15_000,
      }),
    ],
  });

  return trpcClient;
}

function bytesToBase64(data: Uint8Array): string {
  let result = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.subarray(i, i + chunkSize);
    result += String.fromCharCode(...chunk);
  }
  return btoa(result);
}

export async function forwardToPrinter(peerId: string, printerIP: string, payload: string | Uint8Array) {
  const [host, portStr] = printerIP.includes(":") ? printerIP.split(":") : [printerIP, "9100"];
  if(!host) {
    throw new Error("Invalid printer IP");
  }
  const port = Number(portStr) || 9100;
  const client = await getOrCreateDesktopTrpcClient(peerId);
  const encoded = payload instanceof Uint8Array ? bytesToBase64(payload) : payload;
  return client.forward.mutate({ host, port, payload: encoded });
}

/**
 * 测试与 desktop peer 的连接
 */
export async function pingDesktopPeer(peerId: string): Promise<boolean> {
  if (!peerId) {
    return false;
  }
  
  try {
    const client = await getOrCreateDesktopTrpcClient(peerId);
    await client.ping.query();
    return true;
  } catch {
    return false;
  }
}

/**
 * 创建客户信息
 */
export async function createCustomer(peerId: string, data: Record<string, any>) {
  if (!peerId) {
    throw new Error("Peer ID is required");
  }
  
  const client = await getOrCreateDesktopTrpcClient(peerId);
  return client.customers.create.mutate({ data });
}


